import { useContext, useEffect, useState } from "react";
import { useModalAction } from "../modal-views/context";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import GlobalContext from "src/context/global-context";
import { ConversionTransaction, newPaginationInput } from "src/api/types";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import client from "../../api";
import { useMutation } from "react-query";
import {
  convertChannelName,
  getErrorMessage,
  logger,
  makeNumberWithFloatingDigits,
} from "src/lib/utils";
import { ColumnsType } from "antd/es/table";
import { Table } from "antd";
import { EyeIcon } from "../icons/eye-icon";

const WalletTerminalTransactionsPage = ({
  searchText,

  searchField,
  startDate,
  endDate,
  isBuisiness,
  timezone,
}: {
  searchText: string;

  searchField: string;
  startDate?: Date | null;
  endDate?: Date | null;
  isBuisiness?: boolean;
  timezone: string;
}) => {
  const targetModule = isBuisiness
    ? permissionConstants.TRANSACTION_HISTORY_BUSINESS
    : permissionConstants.TRANSACTION_HISTORY_INDIVIDUAL;
  const { canView, canList, canDownload } = usePermissions(targetModule);
  const canViewTransactions = canView || canList;

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { openModal } = useModalAction();

  const [dataSource, setDataSource] = useState<ConversionTransaction[]>([]);
  const [searchParams, setSearchParams] = useState<newPaginationInput>({
    limit: LIST_PAGE_SIZE,
    page: 1,
  });

  const [total, setTotal] = useState(0);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }
  //   logger.log(searchField, " ", searchText, "DEEE###");
  useEffect(() => {
    setSearchParams({
      ...searchParams,
      page: 1,
    });
  }, [searchText, startDate, endDate]);

  const onPageChange = (page: number, pageSize: number) => {
    // logger.log(page, pageSize);
    setSearchParams({
      ...searchParams,
      page: page,
    });
  };
  const { mutate: downloadReceipt, isLoading: downloadReceiptLoading } =
    useMutation(client.transactions.downloadTransactionReceiptPdf, {
      onSuccess: (pdfBlob, variables) => {
        const blob = new Blob([pdfBlob], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `invoice_${variables.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();

        setAlertText("Receipt downloaded successfully");
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          setAlertText(
            error.response
              ? getErrorMessage(error.response.data)
              : APISomethingWrongMsg,
          );
        }
        setIsAlertOpened(true);
      },
    });

  const { mutate: historyLogs, isLoading: historyLogsLoading } = useMutation(
    client.walletTerminalTransactions.viewHistoryLogs,
    {
      onSuccess: (data) => {
        openModal("WALLET_TERMINAL_HISTORY_MODAL", {
          items: data?.items ?? [],
        }); // setAlertText("");
        // setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          setAlertText(
            error.response
              ? getErrorMessage(error.response.data)
              : APISomethingWrongMsg,
          );
        }
        setIsAlertOpened(true);
      },
    },
  );

  const receiptBtn = (record: any) => {
    if (!canDownload) {
      setAlertText("You do not have permission to download receipt");
      setIsAlertOpened(true);
      return;
    }
    setDownloadingId(record.id);
    downloadReceipt(
      {
        type: "wallet_terminal",
        id: record.id,
        timezone: timezone,
      },
      {
        onSettled: () => setDownloadingId(null),
      },
    );
  };

  const viewBtn = (record: any) => {
    if (!canView) return;
    logger.log("RECORD", record);
    // setDownloadingId(record.id);
    historyLogs({
      page: 1,
      limit: 50,
      // orderBy:id,
      sortOrder: "DESC",
      wallet_terminal_history_id: record.id,
    });
  };

  const { mutate: getList, isLoading } = useMutation(
    client.walletTerminalTransactions.getWalletTerminalTransactionsList,
    {
      onSuccess: (data: any) => {
        const dataObj = data;
        setTotal(dataObj.totalcount);
        setDataSource(
          dataObj?.items.map((item: any) => ({
            key: item?.id,
            id: item?.id,
            provider_identifier: item?.provider_identifier,
            user_id: item?.user_id,
            email: item?.user?.email,
            asset_id: item?.asset_id,
            provider_transaction_ref: item?.provider_transaction_ref,
            source: item?.source,
            transfer_type: item?.transfer_type,
            type: item?.type,
            amount: Number(item?.amount ?? 0),
            amount_fee: Number(item?.fee ?? 0),
            total: Number(item?.total ?? 0),
            canceled_reason: item?.canceled_reason ?? null,
            currency: item?.asset?.code,
            status: item?.status,
            description: item?.description,
            created_at: item?.created_at,
            updated_at: item?.updated_at,
          })),
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  useEffect(() => {
    if (!isMounted || !canViewTransactions) {
      return;
    }
    getList({
      ...searchParams,
      start_date: startDate
        ? moment(startDate).format("yyyy-MM-DD")
        : undefined,
      end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
      search_value: searchText?.length > 0 ? searchText : undefined,
      search_field: searchField?.length > 0 ? searchField : undefined,
      //   type: isBuisiness ? "business" : "individual",
      //   timezone: timezone,
    });
  }, [isMounted, canViewTransactions, startDate, endDate, searchParams.page, searchField]);

  useEffect(() => {
    if (!isMounted || !canViewTransactions) {
      return;
    }
    getList({
      ...searchParams,
      page: 1,
      start_date: startDate
        ? moment(startDate).format("yyyy-MM-DD")
        : undefined,
      end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
      search_value: searchText.length > 0 ? searchText : undefined,
      search_field: searchField?.length > 0 ? searchField : undefined,
      //   type: isBuisiness ? "business" : "individual",
      //   timezone: timezone,
    });
  }, [searchText, canViewTransactions]);

  const columns: ColumnsType<ConversionTransaction> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      className: "w-auto",
      key: "id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    //provider_transaction_ref
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Provider transaction Id
        </p>
      ),
      dataIndex: "provider_transaction_ref",
      className: "w-auto",
      key: "provider_transaction_ref",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          User Id
        </p>
      ),
      dataIndex: "user_id",
      className: "w-auto",
      key: "user_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          User Email
        </p>
      ),
      dataIndex: "email",
      className: "w-auto",
      key: "email",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       Provider
    //     </p>
    //   ),
    //   dataIndex: "provider_identifier",
    //   className: "w-auto",
    //   key: "provider_identifier",
    //   render: (value) => (
    //     <div className="flex flex-row gap-2 items-center">
    //       <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
    //         {value ?? "__"}
    //       </p>
    //     </div>
    //   ),
    // },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Source
        </p>
      ),
      dataIndex: "source",
      className: "w-auto",
      key: "source",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Type
        </p>
      ),
      dataIndex: "type",
      className: "w-auto",
      key: "type",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       Transfer type
    //     </p>
    //   ),
    //   dataIndex: "transfer_type",
    //   className: "w-auto",
    //   key: "transfer_type",
    //   render: (value) => (
    //     <div className="flex flex-row gap-2 items-center">
    //       <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
    //         {value ?? "__"}
    //       </p>
    //     </div>
    //   ),
    // },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Amount
        </p>
      ),
      dataIndex: "amount",
      className: "w-auto",
      key: "amount",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {makeNumberWithFloatingDigits(value, 2)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Fee
        </p>
      ),
      dataIndex: "amount_fee",
      className: "w-auto",
      key: "amount_fee",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {makeNumberWithFloatingDigits(value, 2)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Total
        </p>
      ),
      dataIndex: "total",
      className: "w-auto",
      key: "total",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {makeNumberWithFloatingDigits(value, 2)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Currency
        </p>
      ),
      dataIndex: "currency",
      className: "w-auto",
      key: "currency",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value.toUpperCase() ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Date
        </p>
      ),
      dataIndex: "created_at",
      className: "w-auto",
      key: "created_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {moment.unix(value).format("YYYY-MM-DD HH:mm:ss")} */}
            {moment.utc(value).local().format("YYYY-MM-DD HH:mm:ss")}{" "}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {/* {L.translate("TransactionHistory.status")} */}
          Status
        </p>
      ),
      dataIndex: "status",
      className: "w-auto",
      key: "status",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {getPaymentStatusName(fetchStatus(value).toLowerCase())} */}
            {/* {value?.toLowerCase() === "ok" ? "Success" : "Failed"} */}
            {convertChannelName(value)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Cancel Reason
        </p>
      ),
      dataIndex: "canceled_reason",
      className: "w-auto",
      key: "canceled_reason",
      render: (_, record) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {record.status === "failed" || record.status === "rejected"
              ? _ || "—"
              : "—"}{" "}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receipt
        </p>
      ),
      dataIndex: "receipt",
      className: "w-auto",
      key: "receipt",
      render: (_, record) =>
        record.status?.toLowerCase() !== "completed" || !canDownload ? (
          <p className="text-gray-400 text-[12px] font-manrope italic">---</p>
        ) : downloadingId === Number(record.id) ? (
          <p className="text-gray-400 text-[12px] font-manrope italic">
            Downloading...
          </p>
        ) : (
          <a
            onClick={() => receiptBtn(record)}
            className="text-secondary underline hover:text-primary text-[12px] font-manrope font-medium cursor-pointer"
          >
            Receipt
          </a>
        ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      key: "action",
      // fixed: "right",
      width: 60,
      render: (_, record) =>
        canView ? (
          <a
            onClick={() => viewBtn(record)}
            className="text-secondary hover:text-primary cursor-pointer"
          >
            <EyeIcon className="w-4 h-4" />
          </a>
        ) : (
          "—"
        ),
    },
  ];

  if (!canViewTransactions) {
    return (
      <div className="custom-scroll bg-white rounded-[12px] w-full p-6 text-center text-secondary">
        You do not have permission to view transactions.
      </div>
    );
  }

  return (
    <div className="custom-scroll bg-white rounded-[12px] w-full">
      <div className="p-4 relative">
        <Table
          loading={isLoading}
          showSorterTooltip={false}
          columns={columns}
          dataSource={dataSource}
          scroll={{ x: "max-content" }}
          pagination={{
            total,
            current: searchParams.page,
            onChange: onPageChange,
            position: ["bottomCenter"],
            defaultPageSize: LIST_PAGE_SIZE,
            showSizeChanger: false,
          }}
        />
      </div>
    </div>
  );
};

export default WalletTerminalTransactionsPage;
